import React, { useState } from 'react'

import CustomDialog from 'shared/components/CustomDialog'
import { VehicleModalType } from 'views/Authorized/views/Customers/types'
import { VehicleType } from 'shared/store/useCustomers/types'
import {
   addVehicleToCustomerService,
   deleteVehicleService,
   editVehicleService,
} from 'views/Authorized/views/Customers/services'
import { isAxiosError } from 'axios'
import { useNotificationContext } from 'shared/context/Notifications'
import { useTranslation } from 'react-i18next'
import VehicleDetailsModal from '../VehicleDetailsModal'
import DeleteVehicleModal from '../DeleteVehicleModal'

interface Props {
   modalDetails: VehicleModalType
   onClose: () => void
   customerUuid: string
}

export default function Modal({ modalDetails, customerUuid, onClose }: Props) {
   const [isAddingVehicle, setIsAddingVehicle] = useState(false)
   const [isDeletingVehicle, setIsDeletingVehicle] = useState(false)
   const [isEditingVehicle, setIsEditingVehicle] = useState(false)
   const { t } = useTranslation(['TRANSLATION', 'CUSTOMERS'])
   const { addNotification } = useNotificationContext()

   function editVehicle(values: VehicleType) {
      setIsEditingVehicle(true)
      editVehicleService(customerUuid, modalDetails.vehicleDetails!.uuid, values)
         .then(() => {
            addNotification(t('TRANSLATION:successSave'), 'success')
            onClose()
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
            } else {
               addNotification('failedSave', 'error')
            }
         })
         .finally(() => {
            setIsEditingVehicle(false)
         })
   }

   function addVehicle(values: VehicleType) {
      setIsAddingVehicle(true)
      addVehicleToCustomerService(customerUuid, values)
         .then(() => {
            addNotification(t('CUSTOMERS:modal.vehicleAdded'), 'success')
            onClose()
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
            } else {
               addNotification('failedSave', 'error')
            }
         })
         .finally(() => {
            setIsAddingVehicle(false)
         })
   }

   function deleteVehicle() {
      setIsDeletingVehicle(true)
      deleteVehicleService(customerUuid, modalDetails.vehicleDetails!.uuid!)
         .then(() => {
            addNotification(t('CUSTOMERS:modal.vehicleDeleted'), 'success')
            onClose()
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
            } else {
               addNotification('failedSave', 'error')
            }
         })
         .finally(() => {
            setIsDeletingVehicle(false)
         })
   }
   function renderContent() {
      switch (modalDetails.variant) {
         case 'DELETE':
            return (
               <DeleteVehicleModal
                  onDelete={deleteVehicle}
                  isDeleting={isDeletingVehicle}
                  onClose={onClose}
                  vehicleName={`${modalDetails.vehicleDetails!.brand} ${modalDetails.vehicleDetails!.model}`}
               />
            )
         case 'EDIT':
         case 'CREATE':
            return (
               <VehicleDetailsModal
                  onClose={onClose}
                  customerUuid={customerUuid}
                  vehicleDetails={modalDetails.vehicleDetails}
                  vehicleUuid={modalDetails.vehicleDetails?.uuid}
                  onAdd={(values) => addVehicle(values)}
                  onEdit={(values) => editVehicle(values)}
                  isAdding={isAddingVehicle}
                  isEditing={isEditingVehicle}
               />
            )
         default:
            return null
      }
   }

   return (
      <CustomDialog
         maxWidth={modalDetails.variant === 'DELETE' ? undefined : 'lg'}
         isLoading={modalDetails.variant === undefined}
         deleteVariant={modalDetails.variant === 'DELETE'}
         open={modalDetails.open}
         disableClosing
         onClose={onClose}
         fullWidth
         content={renderContent()}
      />
   )
}
